import { esVistaDeAdmin } from '../utils/sesion';
import React, { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ThemeToggle from './ThemeToggle';
import BrandAvatar from './BrandAvatar';
import MarcaApp from './MarcaApp';
import NotificationBell from './NotificationBell';
import InstallPrompt from './InstallPrompt';
import { LayoutDashboard, Database, Users, Megaphone, MessageSquare, BarChart3, Settings, LogOut, X, Bot, Inbox, Plug , Eye} from 'lucide-react';
import { hasFeature } from '../utils/features';
import { useLateral, ItemNav, BotonColapsar, fondoDeMarca, SOBRE_DEGRADE, BarraMobil, TapaCajon } from './lateral';
import api from '../utils/api';

const navItems = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard', end: true, feature: 'dashboard' },
  { to: '/datasets', icon: Database, label: 'Mis Bases', feature: 'datasets' },
  { to: '/leads', icon: Users, label: 'Todos los Leads', feature: 'leads' },
  { to: '/inbox', icon: Inbox, label: 'Conversaciones', feature: 'inbox' },
  { to: '/campaigns', icon: Megaphone, label: 'Campañas', feature: 'campaigns' },
  { to: '/templates', icon: MessageSquare, label: 'Plantillas', feature: 'templates' },
  { to: '/reports', icon: BarChart3, label: 'Reportes', feature: 'reports' },
  { to: '/bot', icon: Bot, label: 'Agente IA', feature: 'bot' },
  { to: '/channels', icon: Plug, label: 'Canales', feature: 'channels' },
  { to: '/settings', icon: Settings, label: 'Configuración', feature: 'settings' },
];

export default function Layout() {
  const { logout, user, tenant, theme, setTheme } = useAuth();
  const [clientName, setClientName] = useState('');
  const lateral = useLateral();

  useEffect(() => {
    if (tenant?.company_name) { setClientName(tenant.company_name); }
    else { api.get('/settings').then(res => setClientName(res.data.client_name || '')).catch(() => {}); }
  }, [tenant]);

  const brandName = tenant?.company_name || tenant?.name || clientName || 'Mi marca';
  const fondo = fondoDeMarca(tenant?.primary_color);
  const BORDE = '1px solid rgba(255,255,255,0.12)';

  /* Si entraste desde el super admin, la barra lo dice. Sin esto es idéntico a
     la sesión del cliente, y trabajar sin darte cuenta en el CRM equivocado es
     un error caro y silencioso. */
  const comoAdmin = esVistaDeAdmin();
  // El CRM propio de la agencia se distingue de los de clientes de un vistazo
  const esAgencia = typeof window !== 'undefined' && window.location.hostname.startsWith('agencia.');
  const deQuien = tenant?.company_name || tenant?.name || 'este cliente';
  // En el celular no entra la frase entera: va la versión corta
  const barra = esAgencia
    ? { fondo: 'linear-gradient(90deg, #6d28d9, #9333ea)', texto: '#fff',
        leyenda: 'Estás en el CRM de la agencia como superadmin', corta: 'Superadmin · CRM de la agencia' }
    : { fondo: '#d2930c', texto: '#241a00',
        leyenda: `Estás en el CRM de ${deQuien} como superadmin`, corta: `Superadmin · ${deQuien}` };

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100vh', overflow:'hidden' }}>
      {comoAdmin && (
        /* El texto va dentro de un span y no suelto: un nodo de texto suelto en un
           flex no se puede achicar y en el celular se salía de la pantalla. */
        <div className="franja-admin" style={{ background: barra.fondo, color: barra.texto }}>
          <Eye size={14} />
          <span className="solo-pc">{barra.leyenda} · la sesión se cierra al cerrar la pestaña</span>
          <span className="solo-cel">{barra.corta}</span>
          {' '}
          <a href="https://admin.udream.app" style={{ color: barra.texto }}>volver al super admin</a>
        </div>
      )}
      <BarraMobil fondo={fondo} abierto={lateral.cajon} onAlternar={lateral.alternar_cajon}>
        <BrandAvatar src={tenant?.logo_url} name={brandName} size={34} />
        <div style={{ minWidth:0 }}>
          <div className="titulo">{brandName}</div>
          <div className="bajada"><MarcaApp /></div>
        </div>
      </BarraMobil>
      {/* El cajón vive acá adentro y no flotando sobre toda la pantalla: así no
          puede taparle nada al encabezado ni a la franja de superadmin. */}
      <div style={{ display:'flex', flex:1, minHeight:0, overflow:'hidden', position:'relative' }}>
      <TapaCajon abierto={lateral.cajon} onClick={lateral.cerrar} />
      <div {...lateral.props}>
      <aside className={'sidebar' + (lateral.abierto ? ' abierto' : '')}
        style={{ width: lateral.ancho, height:'100%', ...fondo, display:'flex', flexDirection:'column',
          position:'absolute', ...SOBRE_DEGRADE }}>
        <div hidden={lateral.celular} style={{ padding: lateral.abierto ? '24px 20px' : '22px 0', borderBottom:BORDE }}>
          <div style={{ display:'flex', alignItems:'center', gap:12, justifyContent: lateral.abierto ? 'flex-start' : 'center' }}>
            <BrandAvatar src={tenant?.logo_url} name={brandName} size={lateral.abierto ? 48 : 38} />
            {lateral.abierto && (
            <div className="sidebar-brand-text" style={{ minWidth:0 }}>
              <div style={{ fontSize:15, fontWeight:800, color:'#fff', lineHeight:1.2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={brandName}>{brandName}</div>
              <div style={{ display:'flex', alignItems:'center', gap:6, marginTop:4, fontSize:11, color:'rgba(255,255,255,0.6)', fontWeight:600 }}>
                <img src="/favicon-32x32.png?v=2" alt="" style={{ width:20, height:20, borderRadius:5, border:'1px solid rgba(255,255,255,0.7)' }} /> <MarcaApp />
              </div>
            </div>
            )}
          </div>
        </div>
        {hasFeature(tenant, user, 'notifications') && (
          <div style={{ padding:'10px 10px 0' }}><NotificationBell abierto={lateral.abierto} /></div>
        )}
        <nav style={{ flex:1, padding:'8px 10px 12px', display:'flex', flexDirection:'column', gap:2, overflowY:'auto' }}>
          {navItems.filter(item => hasFeature(tenant, user, item.feature)).map(item => (
            <ItemNav key={item.to} to={item.to} end={item.end} icono={item.icon} label={item.label}
              abierto={lateral.abierto} onClick={lateral.cerrar} />
          ))}
        </nav>
        <div style={{ padding: lateral.abierto ? '12px 14px 16px' : '12px 10px 16px', borderTop:BORDE }}>
          {!lateral.celular && <BotonColapsar colapsado={lateral.colapsado} onClick={lateral.alternar} abierto={lateral.abierto} />}
          {lateral.abierto && (
            <div style={{ display:'flex', alignItems:'center', gap:8, margin:'10px 0 8px' }}>
              <div style={{ fontSize:13, color:'rgba(255,255,255,0.85)', minWidth:0, flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{user?.display_name || user?.username}</div>
              <ThemeToggle theme={theme} onChange={setTheme} color="rgba(255,255,255,0.8)" />
            </div>
          )}
          <button onClick={logout} className={'nav-item accion' + (lateral.abierto ? '' : ' solo-icono')} title="Cerrar sesión">
            <LogOut size={19}/>{lateral.abierto && <span className="nav-texto">Cerrar sesión</span>}
          </button>
        </div>
      </aside>
      </div>
      <main className="contenido" style={{ flex:1, overflow:'auto', padding:28 }}><Outlet/></main>
      </div>

      <InstallPrompt />
    </div>
  );
}
